import { useParams, useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { db } from '@/lib/firebase';
import { doc, getDoc, updateDoc, increment } from 'firebase/firestore';
import { ArrowLeft, Calendar, Eye, ChevronRight, ChevronLeft, Megaphone } from 'lucide-react';
import { cn } from '@/shared/lib/utils';
import { Notice } from '@/shared/hooks/useNotices';

const NoticeDetailPage: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [notice, setNotice] = useState<Notice | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!id) return;

        const fetchNotice = async () => {
            try {
                const noticeDoc = await getDoc(doc(db, 'announcements', id));
                if (noticeDoc.exists()) {
                    setNotice({ id: noticeDoc.id, ...noticeDoc.data() } as Notice);

                    // 조회수 증가
                    await updateDoc(doc(db, 'announcements', id), {
                        views: increment(1)
                    });
                } else {
                    // 샘플 데이터 (ID가 1이나 2일 경우)
                    if (id === '1') {
                        setNotice({
                            id: '1',
                            title: '코인투개시 정식 서비스 런칭 안내',
                            content: `
                                <h2>안녕하세요, 코인투개시입니다.</h2>
                                
                                <p>글로벌 가상자산 에스크로 플랫폼 <strong>코인투개시</strong>가 드디어 정식 오픈했습니다!</p>
                                
                                <h3>주요 서비스</h3>
                                <ul>
                                    <li><strong>데이 트레이딩</strong>: BTC/USDT 1분 단위 옵션 거래</li>
                                    <li><strong>블록딜</strong>: 대량 거래를 위한 안전한 에스크로 시스템</li>
                                    <li><strong>스테이킹</strong>: 보유 자산으로 이자 수익 창출</li>
                                    <li><strong>P2P 거래</strong>: 사용자 간 직접 거래 지원</li>
                                </ul>
                                
                                <h3>보안 및 안전</h3>
                                <p>고객님의 자산 안전을 최우선으로 하여 다음과 같은 보안 시스템을 구축했습니다:</p>
                                <ul>
                                    <li>실명 인증 시스템</li>
                                    <li>다층 보안 지갑</li>
                                    <li>24/7 모니터링</li>
                                </ul>
                                
                                <p>앞으로도 최고의 서비스를 제공하기 위해 노력하겠습니다.</p>
                                <p>감사합니다.</p>
                            `,
                            date: '2024-02-07',
                            category: '공지',
                            isHot: true
                        });
                    } else if (id === '2') {
                        setNotice({
                            id: '2',
                            title: '시스템 점검 안내 (02/15)',
                            content: `
                                <h2>시스템 점검 안내</h2>
                                
                                <p>안정적인 서비스 제공을 위해 다음과 같이 시스템 점검을 실시합니다.</p>
                                
                                <h3>점검 일시</h3>
                                <p><strong>2024년 2월 15일 (목) 02:00 ~ 06:00 (약 4시간)</strong></p>
                                
                                <h3>점검 내용</h3>
                                <ul>
                                    <li>서버 안정화 작업</li>
                                    <li>데이터베이스 최적화</li>
                                    <li>신규 기능 업데이트 준비</li>
                                </ul>
                                
                                <h3>이용 제한 사항</h3>
                                <ul>
                                    <li>점검 시간 동안 입출금이 일시 중단됩니다.</li>
                                    <li>거래 서비스 이용이 불가합니다.</li>
                                    <li>로그인은 가능하나 조회 기능만 사용 가능합니다.</li>
                                </ul>
                                
                                <p>이용에 불편을 드려 죄송합니다.</p>
                                <p>보다 나은 서비스로 보답하겠습니다.</p>
                            `,
                            date: '2024-02-05',
                            category: '점검'
                        });
                    }
                }
            } catch (error) {
                console.error('공지사항 로딩 실패:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchNotice();
    }, [id]);

    const getCategoryStyle = (cat: string) => {
        switch (cat) {
            case '공지':
            case 'Notice': return 'text-blue-500 bg-blue-500/10 border-blue-500/20';
            case '점검':
            case 'Maintenance': return 'text-yellow-500 bg-yellow-500/10 border-yellow-500/20';
            case '상장':
            case 'Listing': return 'text-emerald-500 bg-emerald-500/10 border-emerald-500/20';
            case '이벤트':
            case 'Event': return 'text-purple-500 bg-purple-500/10 border-purple-500/20';
            case '보안':
            case 'Security': return 'text-red-500 bg-red-500/10 border-red-500/20';
            default: return 'text-slate-500 bg-slate-500/10 border-slate-500/20';
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <Megaphone className="h-12 w-12 text-blue-500 animate-pulse" />
            </div>
        );
    }

    if (!notice) {
        return (
            <div className="min-h-screen flex flex-col items-center justify-center gap-4">
                <p className="text-lg font-bold text-muted-foreground">공지사항을 찾을 수 없습니다.</p>
                <button
                    onClick={() => navigate('/notices')}
                    className="px-6 py-3 bg-blue-600 hover:bg-blue-500 text-white rounded-xl font-bold transition-colors"
                >
                    목록으로 돌아가기
                </button>
            </div>
        );
    }

    return (
        <div className="min-h-screen pb-24 text-foreground">
            <div className="max-w-4xl mx-auto px-4 lg:px-8 space-y-8 pt-8">
                {/* Back Button */}
                <button
                    onClick={() => navigate('/notices')}
                    className="group flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
                >
                    <ArrowLeft className="h-4 w-4 group-hover:-translate-x-1 transition-transform" />
                    <span className="text-sm font-bold uppercase tracking-widest">목록으로</span>
                </button>

                {/* Notice Header */}
                <div className="space-y-6 p-8 rounded-[2.5rem] bg-card border border-border">
                    <div className="flex items-center gap-3">
                        <span className={cn(
                            "px-4 py-1.5 rounded-xl text-[10px] font-black uppercase tracking-[0.2em] border",
                            getCategoryStyle(notice.category)
                        )}>
                            {notice.category}
                        </span>
                        {notice.isHot && (
                            <span className="px-3 py-1 rounded-lg bg-rose-500/10 text-rose-500 text-[8px] font-black uppercase tracking-widest animate-pulse border border-rose-500/20">
                                HOT
                            </span>
                        )}
                    </div>

                    <h1 className="text-3xl md:text-4xl font-black tracking-tight leading-tight">
                        {notice.title}
                    </h1>

                    <div className="flex items-center gap-6 text-xs font-bold text-muted-foreground uppercase tracking-wider">
                        <span className="flex items-center gap-2">
                            <Calendar className="h-3.5 w-3.5" />
                            {notice.date}
                        </span>
                        <span className="w-1 h-1 rounded-full bg-muted-foreground/30"></span>
                        <span className="flex items-center gap-2">
                            <Eye className="h-3.5 w-3.5" />
                            {(notice as any).views || 0} views
                        </span>
                    </div>
                </div>

                {/* Notice Content */}
                <div className="p-8 md:p-12 rounded-[2.5rem] bg-card border border-border">
                    <div
                        className="prose prose-slate dark:prose-invert max-w-none
                            prose-headings:font-black prose-headings:tracking-tight
                            prose-h2:text-2xl prose-h2:mt-8 prose-h2:mb-4
                            prose-h3:text-xl prose-h3:mt-6 prose-h3:mb-3
                            prose-p:text-sm prose-p:leading-relaxed prose-p:my-4
                            prose-ul:my-4 prose-li:text-sm prose-li:my-2
                            prose-strong:text-blue-600 dark:prose-strong:text-blue-500 whitespace-pre-wrap"
                        dangerouslySetInnerHTML={{ __html: notice.content }}
                    />
                </div>

                {/* Navigation */}
                <div className="flex items-center justify-between gap-4 p-6 rounded-2xl bg-card border border-border">
                    <button
                        onClick={() => navigate('/notices')}
                        className="flex items-center gap-2 px-6 py-3 rounded-xl bg-accent hover:bg-foreground hover:text-background text-sm font-bold uppercase tracking-widest transition-all"
                    >
                        <ChevronLeft className="h-4 w-4" />
                        이전
                    </button>
                    <button
                        onClick={() => navigate('/notices')}
                        className="flex items-center gap-2 px-6 py-3 rounded-xl bg-accent hover:bg-foreground hover:text-background text-sm font-bold uppercase tracking-widest transition-all"
                    >
                        다음
                        <ChevronRight className="h-4 w-4" />
                    </button>
                </div>
            </div>
        </div>
    );
};

export default NoticeDetailPage;
